import { useQuery } from '@tanstack/react-query'
import axios, { AxiosRequestConfig } from 'axios'
import authHeader from '../../context/services/AuthHeader'
import {
  PUBLIC_API_SITE_URL,
  API_URL,
  API_CUSTOMERS_URL,
} from '../../context/apiconfig'
import {
  bannerKeys,
  featuredTilesKeys,
  landingPageSavedCartsKeys,
  landingPageOpenOrdersKeys,
  activeRedirectsKeys,
  socialMediaKeys,
  marketingPageKeys,
  marketingPostKeys,
  marketingPageStageRouteKeys,
  marketingPostStageRouteKeys,
  prolineInfoKeys,
  prolinePricingKeys,
  dealerMapKeys,
  followRedirectKeys,
  optionKeys,
  initRegistrationDataKeys,
  storefrontKeys,
} from './keyFactory'
import { orderStatusTypeOpenIds } from '../../constants/db'
import { PROJECT_ID } from '../../helpers'
import { ICartFilter } from '../../context/products/types'
import { IRadioOption } from '../../types/interfaces'
import { IRegisterData } from '../user/types'

const bannersDataStaleTime = 5 * 60 * 60 * 1000 // 5hrs
const socialMediaDataStaleTime = 5 * 60 * 60 * 1000 // 5hrs
const featuredTilesDataStaleTime = 5 * 60 * 60 * 1000 // 5hrs
const activeRedirectsDataStaleTime = 10 * 60 * 1000 // 10 minutes
const marketingPageStaleTime = 1 * 60 * 60 * 1000 // 1hrs

// const filterListsDataStaleTime = 0 // default is 0 //? for debugging

// const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function useWholesaleBannersData() {
  return useQuery({
    queryKey: bannerKeys.loggedIn(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-carousel-images/rw_landing`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: bannersDataStaleTime,
  })
}

export function useProlineBannersData() {
  return useQuery({
    queryKey: bannerKeys.loggedOut(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-carousel-images/pl_landing`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: bannersDataStaleTime,
  })
}

export function useProlineAboutPageBannersData() {
  return useQuery({
    queryKey: bannerKeys.loggedOut(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-carousel-images/pl-about-page-banners`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: bannersDataStaleTime,
  })
}

export function useFeaturedTilesBannersData() {
  return useQuery({
    queryKey: featuredTilesKeys.featuredTiles(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-featured-tiles?project_id=${PROJECT_ID}`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: featuredTilesDataStaleTime,
  })
}

export function useSocialMediaData() {
  return useQuery({
    queryKey: socialMediaKeys.socialMedia(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-social-media?project_id=${PROJECT_ID}`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: socialMediaDataStaleTime,
  })
}

export function useActiveRedirectsData() {
  return useQuery({
    queryKey: activeRedirectsKeys.activeRedirects(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-active-redirects`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: activeRedirectsDataStaleTime,
  })
}

export function useFollowRedirect(url: string) {
  return useQuery({
    queryKey: followRedirectKeys.followRedirect(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${PUBLIC_API_SITE_URL}follow-redirect`,
        { url: url },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: activeRedirectsDataStaleTime,
  })
}

export function useSavedCartsWithItemsLandingData(
  limit?: number,
  filter?: ICartFilter,
) {
  return useQuery({
    queryKey: landingPageSavedCartsKeys.savedCarts(filter, limit),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${API_URL}carts/select-saved-carts-with-items`,
        {
          limit: limit,
          filter: filter,
          project_id: PROJECT_ID,
        },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: featuredTilesDataStaleTime,
  })
}

export function useCustomerOpenOrdersLanding(
  limit?: number,
  statuses?: Array<number>,
) {
  const selectStatuses = statuses ? statuses : orderStatusTypeOpenIds
  return useQuery({
    queryKey: landingPageOpenOrdersKeys.openOrders(limit, selectStatuses),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${API_CUSTOMERS_URL}select-orders`,
        {
          limit: limit,
          statuses: selectStatuses,
        },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: featuredTilesDataStaleTime,
  })
}

export function useMarketingPage(url: string, preview: string | null) {
  return useQuery({
    queryKey: marketingPageKeys.marketingPage(url, preview),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${PUBLIC_API_SITE_URL}get-marketing-page`,
        {
          url: url,
          preview: preview,
          project_id: PROJECT_ID,
        },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useMarketingPages() {
  return useQuery({
    queryKey: marketingPageKeys.marketingPages(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${PUBLIC_API_SITE_URL}get-marketing-pages`,
        { project_id: PROJECT_ID },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useMarketingPost(url: string, preview: string | null) {
  return useQuery({
    queryKey: marketingPostKeys.marketingPost(url, preview),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${PUBLIC_API_SITE_URL}get-marketing-post`,
        {
          url: url,
          preview: preview,
          project_id: PROJECT_ID,
        },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useMarketingPosts() {
  return useQuery({
    queryKey: marketingPostKeys.marketingPosts(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.post(
        `${PUBLIC_API_SITE_URL}get-marketing-posts`,
        { project_id: PROJECT_ID },
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useMarketingPageStageRoutes() {
  return useQuery({
    queryKey: marketingPageStageRouteKeys.marketingPageStageRoutes(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-marketing-page-stage-routes`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useMarketingPostStageRoutes() {
  return useQuery({
    queryKey: marketingPostStageRouteKeys.marketingPostStageRoutes(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-marketing-post-stage-routes`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useGetStorefrontData(
  mode: string | undefined,
  brand: string | undefined,
) {
  return useQuery({
    queryKey: bannerKeys.storefrontBanner(mode, brand),
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (mode) params.mode = mode
      if (brand) params.brand = brand
      const headersValue: AxiosRequestConfig = { headers: authHeader(), params }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-storefront-data`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useGetStorefrontList() {
  return useQuery({
    queryKey: storefrontKeys.list(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-storefront-list`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useProlineInformationOptionsData() {
  return useQuery({
    queryKey: prolineInfoKeys.prolineInfo(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-menu-items/pl_information_options`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useProlinePricingOptionsData() {
  return useQuery({
    queryKey: prolinePricingKeys.prolinePricing(),
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-menu-items/pl_pricing_options`,
        headersValue,
      )
      return response.data
    },
    keepPreviousData: true,
    staleTime: marketingPageStaleTime,
  })
}

export function useGetDealerMapData(
  map_id: string | number | undefined,
  mode?: 'all',
) {
  const hasMapId =
    map_id !== undefined && map_id !== null && `${map_id}` !== ''
  const key = mode === 'all' ? 'all' : map_id
  const hasKey = mode === 'all' || hasMapId
  const mapIdValue = hasMapId ? `${map_id}` : ''
  return useQuery({
    queryKey: dealerMapKeys.dealerMaps(hasKey ? `${key}` : 'missing'),
    enabled: hasKey,
    queryFn: async () => {
      const headersValue: AxiosRequestConfig = { headers: authHeader() }
      let response

      if (mode === 'all') {
        response = await axios.post(
          `${PUBLIC_API_SITE_URL}get-dealer-map-data`,
          {},
          { ...headersValue, params: { mode } },
        )
      } else {
        response = await axios.post(
          `${PUBLIC_API_SITE_URL}get-dealer-map-data/${mapIdValue}`,
          {},
          headersValue,
        )
      }
      return response.data
    },
    keepPreviousData: false,
    staleTime: marketingPageStaleTime,
  })
}

export function useCustomerTypeOptions(is_registration: boolean) {
  return useQuery<IRadioOption[], Error>({
    queryKey: optionKeys.customerTypeOptions(is_registration),
    queryFn: async () => {
      const response = await axios.post(
        `${PUBLIC_API_SITE_URL}get-customer-type-options`,
        { is_registration },
      )
      return response.data
    },
    staleTime: 1 * 60 * 60 * 1000,
  })
}

export function useInitRegistrationData() {
  return useQuery<IRegisterData, Error>({
    queryKey: initRegistrationDataKeys.initRegistrationData(),
    queryFn: async () => {
      const response = await axios.get(
        `${PUBLIC_API_SITE_URL}get-init-reg-data`,
      )
      return response.data
    },
    staleTime: 3 * 1000,
  })
}
